import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CreditCard, Plus, Eye, Search, Filter,
  CheckCircle, XCircle, ChevronLeft, ChevronRight,
  Calendar, DollarSign, Package, User, AlertTriangle,
  Clock, TrendingUp, Activity, Info, Download, Upload
} from 'lucide-react';
import toast from 'react-hot-toast';
import subscriptionService from '../services/subscriptionService';
import pricingPlanService from '../services/pricingPlanService';
import { useDebounce } from '../hooks/useDebounce';
import { useAuth } from '../contexts/AuthContext';
import { TableLoader } from '../components/CommonLoader';
import FilterBar from '../components/FilterBar';
import StandardDataTable from '../components/StandardDataTable';
import EnhancedDataTable from '../components/EnhancedDataTableV2';
import { CreateButton } from '../components/CommonButtons';
import Switch from '../components/Switch';

const Subscriptions = () => {
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  // Removed activeTab state as we're using dropdown filter instead
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPlanId, setFilterPlanId] = useState('');
  const [filterAutoRenew, setFilterAutoRenew] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [totalSubscriptions, setTotalSubscriptions] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showStorageModal, setShowStorageModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [subscriptionPeriods, setSubscriptionPeriods] = useState([]);
  const [storageData, setStorageData] = useState({
    add_on_storage: '',
    notes: ''
  });
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    pending: 0,
    cancelled: 0,
    expired: 0,
    revenue: 0
  });

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Check if user is super admin
  const isSuperAdmin = user?.roles?.some(role => {
    // Handle both string and object role formats
    const roleName = typeof role === 'string' ? role : (role?.name || role?.role || '');
    return ['super-admin', 'superadmin', 'super_admin'].includes(roleName.toLowerCase());
  });

  useEffect(() => {
    fetchSubscriptions();
  }, [currentPage, debouncedSearchTerm, filterStatus, filterPlanId, filterAutoRenew, perPage, sortBy, sortOrder]);

  // Fetch available plans only for super admin users
  useEffect(() => {
    if (isSuperAdmin) {
      fetchAvailablePlans();
    }
  }, [isSuperAdmin]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: perPage,
        search: debouncedSearchTerm,
        status: filterStatus,
        pricing_plan_id: filterPlanId,
        auto_renew: filterAutoRenew,
        sort_by: sortBy,
        sort_order: sortOrder
      };

      const response = await subscriptionService.getAllSubscriptions(params);
      
      // Handle response structure
      const subscriptionsList = response.subscriptions || response.data || [];
      
      setSubscriptions(subscriptionsList);
      
      if (response.pagination) {
        setTotalSubscriptions(response.pagination.total || 0);
        setTotalPages(response.pagination.last_page || 1);
        setCurrentPage(response.pagination.current_page || 1);
      }

      // Update stats if available
      if (response.stats) {
        setStats(response.stats);
      } else {
        // Calculate stats from subscriptions
        const statsData = {
          total: subscriptionsList.length,
          active: subscriptionsList.filter(s => s.status === 'active').length,
          expired: subscriptionsList.filter(s => s.status === 'expired').length,
          canceled: subscriptionsList.filter(s => s.status === 'canceled').length,
          revenue: subscriptionsList.reduce((sum, s) => sum + (s.pricing_plan?.price || 0), 0)
        };
        setStats(statsData);
      }
    } catch (error) {
      toast.error('Failed to fetch subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailablePlans = async () => {
    try {
      const response = await pricingPlanService.getPricingPlans({ per_page: 100 });
      const plansList = response.pricingPlans || response.data || [];
      setAvailablePlans(plansList);
    } catch (error) {
    }
  };

  const fetchSubscriptionDetails = async (subscription) => {
    try {
      const details = await subscriptionService.getSubscriptionDetails(subscription.id);
      setSelectedSubscription(details.data || details);
      
      // Fetch subscription periods
      const periodsResponse = await subscriptionService.getSubscriptionPeriods(subscription.id);
      setSubscriptionPeriods(periodsResponse.periods || periodsResponse.data || []);
      
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to fetch subscription details');
    }
  };

  const handleToggleAutoRenew = async (subscription) => {
    try {
      const currentAutoRenew = subscription.auto_renew === 1 || subscription.auto_renew === true;
      const newAutoRenewValue = currentAutoRenew ? 0 : 1;
      
      await subscriptionService.toggleAutoRenew(subscription.id, newAutoRenewValue);
      toast.success(`Auto-renew ${currentAutoRenew ? 'disabled' : 'enabled'} successfully`);
      fetchSubscriptions();
    } catch (error) {
      toast.error('Failed to toggle auto-renew');
    }
  };

  const handleCancelSubscription = async (subscription) => {
    if (!window.confirm('Are you sure you want to cancel this subscription? This action cannot be undone.')) {
      return;
    }

    const reason = window.prompt('Please provide a reason for cancellation:');
    if (!reason) return;

    try {
      await subscriptionService.cancelSubscription(subscription.id, reason);
      toast.success('Subscription canceled successfully');
      fetchSubscriptions();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  const handleAddStorage = async (e) => {
    e.preventDefault();
    
    if (!selectedSubscription || !storageData.add_on_storage) {
      toast.error('Please enter storage amount');
      return;
    }

    try {
      await subscriptionService.addOnStorage(
        selectedSubscription.user_id,
        parseFloat(storageData.add_on_storage),
        storageData.notes
      );
      toast.success('Storage added successfully');
      setShowStorageModal(false);
      setStorageData({ add_on_storage: '', notes: '' });
      fetchSubscriptions();
    } catch (error) {
      toast.error('Failed to add storage');
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getStatusBadge = (status) => {
    // Handle different status formats (number, string, or object with status_text)
    let statusText = '';
    let statusKey = '';
    
    if (typeof status === 'number') {
      // Handle numeric status: 0=Inactive, 1=Active, 2=Pending Cancellation, 3=Cancelled, 4=Expired
      switch(status) {
        case 1:
          statusKey = 'active';
          statusText = 'Active';
          break;
        case 0:
          statusKey = 'inactive';
          statusText = 'Inactive';
          break;
        case 2:
          statusKey = 'pending';
          statusText = 'Pending Cancellation';
          break;
        case 3:
          statusKey = 'cancelled';
          statusText = 'Cancelled';
          break;
        case 4:
          statusKey = 'expired';
          statusText = 'Expired';
          break;
        default:
          statusKey = 'inactive';
          statusText = 'Unknown';
      }
    } else if (typeof status === 'string') {
      statusKey = status.toLowerCase();
      statusText = status.charAt(0).toUpperCase() + status.slice(1);
    } else if (status && typeof status === 'object' && status.status_text) {
      statusKey = status.status_text.toLowerCase();
      statusText = status.status_text;
    } else {
      // Default fallback
      statusKey = 'inactive';
      statusText = 'Unknown';
    }

    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      inactive: { color: 'bg-gray-100 text-gray-800', icon: XCircle },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle },
      canceled: { color: 'bg-red-100 text-red-800', icon: XCircle }, // Alternative spelling
      expired: { color: 'bg-red-100 text-red-800', icon: XCircle }
    };

    const config = statusConfig[statusKey] || statusConfig.inactive;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {statusText}
      </span>
    );
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Subscriptions Management</h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage all user subscriptions and billing
        </p>
      </div>


      {/* Filters and Search */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by dataroom, user name, email, or plan..."
                className="w-full pl-10 pr-4 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.25rem'
              }}
            >
              <option value="">All Status</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
              <option value="2">Pending Cancellation</option>
              <option value="3">Cancelled</option>
              <option value="4">Expired</option>
            </select>

            {isSuperAdmin && (
              <select
                value={filterPlanId}
                onChange={(e) => setFilterPlanId(e.target.value)}
                className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                  backgroundPosition: 'right 0.5rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.25rem'
                }}
              >
                <option value="">All Plans</option>
                {availablePlans.map(plan => (
                  <option key={plan.id} value={plan.id}>{plan.name}</option>
                ))}
              </select>
            )}

            <select
              value={filterAutoRenew}
              onChange={(e) => setFilterAutoRenew(e.target.value)}
              className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.25rem'
              }}
            >
              <option value="">Auto-Renew</option>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </div>
      </div>

      {/* Subscriptions Table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <EnhancedDataTable
          data={subscriptions}
          columns={[
            {
              key: 'data_room',
              header: 'Dataroom',
              sortable: true,
              render: (value, row) => (
                <div className="flex items-center">
                  <Package className="h-5 w-5 text-gray-400 mr-2" />
                  <div className="text-sm text-gray-900">
                    {row.data_room?.name || 'N/A'}
                  </div>
                </div>
              )
            },
            {
              key: 'pricing_plan',
              header: 'Plan',
              sortable: true,
              render: (value, row) => (
                <div className="text-sm text-gray-900">
                  {row.pricing_plan?.name || 'N/A'}
                </div>
              )
            },
            {
              key: 'status',
              header: 'Status',
              sortable: true,
              render: (value, row) => getStatusBadge(row.status_text || row.status)
            },
            {
              key: 'start_date',
              header: 'Start Date',
              sortable: true,
              render: (value, row) => (
                <span className="text-sm text-gray-500">
                  {(row.current_period_starts_at || row.start_date)
                    ? new Date(row.current_period_starts_at || row.start_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })
                    : 'N/A'
                  }
                </span>
              )
            },
            {
              key: 'end_date',
              header: 'End Date',
              sortable: true,
              render: (value, row) => (
                <span className="text-sm text-gray-500">
                  {(row.current_period_ends_at || row.end_date)
                    ? new Date(row.current_period_ends_at || row.end_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })
                    : 'N/A'
                  }
                </span>
              )
            },
            {
              key: 'auto_renew',
              header: 'Auto-Renew',
              sortable: false,
              render: (value, row) => (
                <Switch
                  checked={row.auto_renew === 1 || row.auto_renew === true}
                  onChange={() => handleToggleAutoRenew(row)}
                  tooltip={row.auto_renew ? 'Click to disable auto-renew' : 'Click to enable auto-renew'}
                />
              )
            },
            {
              key: 'price',
              header: 'Price',
              sortable: true,
              render: (value, row) => (
                <span className="text-sm text-gray-900">
                  ₹{row.pricing_plan?.price || '0'}/month
                </span>
              )
            }
          ]}
          actions={(row) => [
            {
              icon: Eye,
              label: 'View Details',
              onClick: () => fetchSubscriptionDetails(row)
            },
            ...(isSuperAdmin ? [
              {
                icon: Upload,
                label: 'Add Storage',
                onClick: () => {
                  setSelectedSubscription(row);
                  setShowStorageModal(true);
                },
                className: 'text-green-600 hover:text-green-800'
              }
            ] : []),
            // Auto Renew Off option for active subscriptions with auto-renew enabled
            ...(row.status === 1 && (row.auto_renew === 1 || row.auto_renew === true) ? [
              {
                icon: AlertTriangle,
                label: 'Auto Renew Off',
                onClick: () => handleToggleAutoRenew(row),
                className: 'text-orange-600 hover:text-orange-800'
              }
            ] : []),
            ...(row.status === 1 ? [
              {
                icon: XCircle,
                label: 'Cancel Subscription',
                onClick: () => handleCancelSubscription(row),
                className: 'text-red-600 hover:text-red-800'
              }
            ] : [])
          ]}
          loading={loading}
          emptyMessage="No subscriptions found"
          emptyIcon={CreditCard}
          showColumnControls={true}
          enableColumnResize={true}
          enableDragDrop={true}
          onSort={(key, direction) => {
            const sorted = [...subscriptions].sort((a, b) => {
              let aVal = a[key];
              let bVal = b[key];
              
              if (key === 'pricing_plan') {
                aVal = a.pricing_plan?.name || '';
                bVal = b.pricing_plan?.name || '';
              } else if (key === 'price') {
                aVal = a.pricing_plan?.price || 0;
                bVal = b.pricing_plan?.price || 0;
              } else if (key === 'start_date' || key === 'end_date') {
                const dateFieldA = key === 'start_date' ? 
                  (a.current_period_starts_at || a.start_date) : 
                  (a.current_period_ends_at || a.end_date);
                const dateFieldB = key === 'start_date' ? 
                  (b.current_period_starts_at || b.start_date) : 
                  (b.current_period_ends_at || b.end_date);
                aVal = dateFieldA ? new Date(dateFieldA).getTime() : 0;
                bVal = dateFieldB ? new Date(dateFieldB).getTime() : 0;
              }
              
              if (aVal < bVal) return direction === 'asc' ? -1 : 1;
              if (aVal > bVal) return direction === 'asc' ? 1 : -1;
              return 0;
            });
            setSubscriptions(sorted);
          }}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * perPage + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * perPage, totalSubscriptions)}
                  </span>{' '}
                  of <span className="font-medium">{totalSubscriptions}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-3 py-1.5 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-3 py-1.5 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Subscription Detail Modal */}
      {showDetailModal && selectedSubscription && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Subscription Details</h3>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedSubscription(null);
                    setSubscriptionPeriods([]);
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {/* Subscription Info */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-4">Subscription Information</h4>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">Dataroom:</span> {selectedSubscription.data_room?.name || 'N/A'}</p>
                    <p><span className="text-gray-500">Plan:</span> {selectedSubscription.pricing_plan?.name}</p>
                    <p><span className="text-gray-500">Price:</span> ₹{selectedSubscription.pricing_plan?.price}/month</p>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">Status:</span> {getStatusBadge(selectedSubscription.status_text || selectedSubscription.status)}</p>
                    <p><span className="text-gray-500">Auto-Renew:</span> {(selectedSubscription.auto_renew === 1 || selectedSubscription.auto_renew === true) ? 'Enabled' : 'Disabled'}</p>
                  </div>
                </div>
              </div>

              {/* Subscription Periods */}
              {subscriptionPeriods.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-4">Subscription Periods</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                          <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                          <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">End Date</th>
                          <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {subscriptionPeriods.map((period, index) => (
                          <tr key={period.id}>
                            <td className="px-3 py-1.5 text-sm">Period {index + 1}</td>
                            <td className="px-3 py-1.5 text-sm">
                              {period.start_date || period.current_period_starts_at
                                ? new Date(period.start_date || period.current_period_starts_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })
                                : 'N/A'
                              }
                            </td>
                            <td className="px-3 py-1.5 text-sm">
                              {period.end_date || period.current_period_ends_at
                                ? new Date(period.end_date || period.current_period_ends_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })
                                : 'N/A'
                              }
                            </td>
                            <td className="px-3 py-1.5 text-sm">{getStatusBadge(period.status_text || period.status)}</td>
                            <td className="px-3 py-1.5 text-sm">₹{period.amount || selectedSubscription.pricing_plan?.price}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Storage Modal (Super Admin Only) */}
      {showStorageModal && selectedSubscription && isSuperAdmin && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          >
            <h3 className="text-lg font-semibold mb-4">Add Storage</h3>
            
            <form onSubmit={handleAddStorage}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  User
                </label>
                <p className="text-sm text-gray-900">
                  {selectedSubscription.user?.first_name} {selectedSubscription.user?.last_name} ({selectedSubscription.user?.email})
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Storage (GB) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={storageData.add_on_storage}
                  onChange={(e) => setStorageData({ ...storageData, add_on_storage: e.target.value })}
                  className="w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500"
                  placeholder="e.g., 50.5"
                  required
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={storageData.notes}
                  onChange={(e) => setStorageData({ ...storageData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-primary-500"
                  placeholder="Enter notes about this storage addition..."
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowStorageModal(false);
                    setStorageData({ add_on_storage: '', notes: '' });
                  }}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white rounded-md hover:bg-blue-700 px-3 py-1.5 text-sm"
                >
                  Add Storage
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Subscriptions;